import { useNavigate, useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { type Priority } from '../../../../models/Priority'
import { GetPlanOrganizationTaskById, GetPriority } from '../../../../services/AnnualPlanService'
import { CircularProgress, Typography } from '@mui/material'
import { PriorityOneOverview } from './PriorityOne/PriorityOneOverview'
import { PriorityThreeOverview } from './PriorityThree/PriorityThreeOverview'
import { generatePath } from 'react-router'
import { AppRoutes } from '../../../Routes'
import { type PlanOrganizationTask } from '../../../../models/PlanOrganizationTask'
import { AnnualPlanTabs } from '../PlanningDetails'
import { PriorityTwoOverview } from './priorityTwo/PriorityTwoOverview'
import { PriorityFourOverview } from './priorityFour/PriorityFourOverview'

interface Props {
  onUpdateStatus?: () => void
}

export const PlanningPriorityActivitiesPlanOrganizationPriority = (props: Props): JSX.Element => {
  const nav = useNavigate()
  const { rcnId, planId, planOrgId, planOrgTaskId } = useParams()
  const [planOrganizationTask, setPlanOrganizationTask] = useState<PlanOrganizationTask | null>(null)
  const [priority, setPriority] = useState<Priority | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const isImplementationPhase = window.location.pathname.includes('Implementation')

  const fetchPriorityAndPlanOrganizationTask = async (): Promise<void> => {
    if (planOrgTaskId == null || isNaN(Number(planOrgTaskId))) {
      console.error('Invalid Plan Organization Task ID.')
      return
    }

    try {
      setIsLoading(true)

      const [priorityResult, planOrgTaskResult] = await Promise.all([GetPriority(Number(planOrgTaskId)), GetPlanOrganizationTaskById(Number(planOrgTaskId))])

      handleFetchResults(priorityResult, planOrgTaskResult)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'An unknown error occurred while fetching the priority.')
      console.error(e)
    } finally {
      setIsLoading(false)
    }
  }

  const handleFetchResults = (
    priorityResult: { success: boolean, response: Priority | null },
    planOrganizationTaskResult: { success: boolean, response: PlanOrganizationTask | null }
  ): void => {
    const { success: prioritySuccess, response: priority } = priorityResult
    const { success: planOrgTaskSuccess, response: planOrgTask } = planOrganizationTaskResult

    if (prioritySuccess && planOrgTaskSuccess) {
      setError(priority == null ? 'The priority was not found.' : null)
      setPriority(priority ?? null)

      setError(planOrgTask == null ? 'The plan organization task was not found.' : null)
      setPlanOrganizationTask(planOrgTask ?? null)
    } else {
      setError(`An error occurred while fetching the ${prioritySuccess ? 'priority' : 'plan organization task'}.`)
    }
  }

  useEffect(() => {
    void fetchPriorityAndPlanOrganizationTask()
  }, [planOrgTaskId])

  if (isLoading) {
    return <CircularProgress data-testid='mainPrioritySpinner'/>
  }

  if (error != null || priority == null || planOrganizationTask == null) {
    return <Typography variant='body1'>{error}</Typography>
  }

  const handleBack = (): void => {
    if (rcnId == null || planId == null || planOrgId == null) {
      return
    }

    const route = generatePath(
      isImplementationPhase
        ? AppRoutes.IMPLEMENTATION_PRIORITY_ACTIVITIES_PLAN_ORGANIZATION
        : AppRoutes.PLANNING_PRIORITY_ACTIVITIES_PLAN_ORGANIZATION,
      { rcnId, planId, tab: String(AnnualPlanTabs.PriorityActivities), planOrgId }
    )
    nav(route)
  }

  const onPlanOrgTaskUpdate = (updatedTask: PlanOrganizationTask): void => {
    setPlanOrganizationTask(updatedTask)
    void fetchPriorityAndPlanOrganizationTask()
    props.onUpdateStatus?.()
  }

  return <>
    {priority.priorityNumber === 1 &&
      <PriorityOneOverview
        priority={priority}
        planOrganizationTask={planOrganizationTask}
        onBack={handleBack}
        onPlanOrgTaskUpdate={onPlanOrgTaskUpdate}
        implementationPhase={isImplementationPhase}
      />
    }

    {priority.priorityNumber === 2 &&
      <PriorityTwoOverview
        priority={priority}
        planOrganizationTask={planOrganizationTask}
        onBack={handleBack}
        onPlanOrgTaskUpdate={onPlanOrgTaskUpdate}
        implementationPhase={isImplementationPhase}
      />
    }

    {priority.priorityNumber === 3 &&
      <PriorityThreeOverview
        priority={priority}
        planOrganizationTask={planOrganizationTask}
        onBack={handleBack}
        onPlanOrgTaskUpdate={onPlanOrgTaskUpdate}
        implementationPhase={isImplementationPhase}
      />
    }

    {priority.priorityNumber === 4 &&
      <PriorityFourOverview
        priority={priority}
        planOrganizationTask={planOrganizationTask}
        onBack={handleBack}
        onPlanOrgTaskUpdate={onPlanOrgTaskUpdate}
        implementationPhase={isImplementationPhase}
      />
    }
  </>
}
